/**
 * Pasos del tour guiado del Expediente (Sprint 11 polish).
 *
 * Mismo patrón que el tour del Radar: una vez por usuario, persistido
 * en localStorage con storageKey por user.id. Los targets apuntan a
 * `data-tour-id` que las secciones renderizan.
 *
 * Hay dos variantes porque hay dos expedientes distintos (#627):
 *
 * - Desde convocatoria: BPIN, checklist y controles de estado están
 *   montados desde el primer día. El copy de bienvenida los anuncia.
 * - Idea-first (`opportunity_id === null`): el trabajo arranca en la
 *   pestaña Formular; el checklist y el cambio de estado no aparecen
 *   hasta confirmar los módulos de identificación. Prometerle "las 3
 *   secciones clave, el checklist y los documentos" describía un
 *   expediente que ese usuario no tiene.
 *
 * Los pasos cuyo target no está en el DOM los salta `OnboardingTour`,
 * que además cuenta solo los visibles en "Paso N de M".
 */

import type { TourStep } from "@/components/OnboardingTour";

const WELCOME_CONVOCATORIA: TourStep = {
  id: "welcome",
  title: "Tu expediente, paso a paso",
  body: "Aquí trabajas todo lo necesario para presentar el proyecto: lo que falta del checklist, los documentos que adjuntas, el avance del estado. Te muestro las 3 secciones clave en 30 segundos.",
};

const WELCOME_IDEA_FIRST: TourStep = {
  id: "welcome",
  title: "Tu expediente, paso a paso",
  body: "Aquí formulas tu idea módulo a módulo y, cuando esté lista, la preparas para radicar. Te muestro las secciones clave en 30 segundos.",
};

const STEP_BPIN: TourStep = {
  id: "bpin",
  target: "[data-tour-id='bpin-section']",
  placement: "bottom",
  title: "Código BPIN",
  body: "Cuando radiques el proyecto en PIIP, registra acá el código BPIN de 14 dígitos. Es el identificador oficial que vincula tu expediente con el banco de proyectos del Estado.",
};

const STEP_FORMULACION: TourStep = {
  id: "formulacion",
  target: "[data-tour-id='formulation-section']",
  placement: "top",
  title: "La formulación",
  body: "Cada módulo es una parte del proyecto: el problema, la población, el objetivo, las alternativas. Cuando confirmes los seis módulos de identificación se habilita el paso a radicar.",
};

const STEP_CHECKLIST: TourStep = {
  id: "checklist",
  target: "[data-tour-id='checklist']",
  placement: "top",
  title: "Checklist documental",
  body: "Cada sector tiene su lista de documentos obligatorios y opcionales. Marca cada item cuando lo tengas y adjunta el archivo respectivo. La barra superior muestra cuánto falta.",
};

const STEP_STATUS: TourStep = {
  id: "status",
  target: "[data-tour-id='status-controls']",
  placement: "top",
  title: "Avanzar de estado",
  body: "Cuando termines de preparar el expediente, mueves su estado: Borrador → Radicado → Elegible → Aprobado. Cada transición queda registrada en el timeline con fecha y notas.",
};

export function buildExpedienteTourSteps({ isIdeaFirst }: { isIdeaFirst: boolean }): TourStep[] {
  if (isIdeaFirst) {
    // Orden de la página: el BPIN vive en el encabezado, la formulación
    // en la pestaña activa. Checklist y estado van al final por si el
    // usuario entró directo a la pestaña Radicar; si no, se saltan.
    return [WELCOME_IDEA_FIRST, STEP_BPIN, STEP_FORMULACION, STEP_CHECKLIST, STEP_STATUS];
  }
  return [WELCOME_CONVOCATORIA, STEP_BPIN, STEP_CHECKLIST, STEP_STATUS];
}
